import React, { memo, useMemo } from 'react';
import { TableBudget } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import {
  differencesToHighlight,
  getQueryStringValue,
  resolveBudgetSections,
} from 'containers/RentForecast/utils';
import type {
  BudgetType,
  SectionConfiguration,
} from 'containers/RentForecast/RDBudgetModal/types';

export const generateHeaders = (
  differenceCount: number,
  budgetType: BudgetType,
) => [
  {
    label: 'Modified After Intacct sync',
    subLabel: `${differenceCount} total items across Parts I, II, III, and V`,
    legend: { backgroundColor: 'warning.lighter' },
    flexSize: 1.4,
    textAlign: 'left',
    sticky: true,
    hidden: budgetType === 'custom',
    hideHeader: budgetType === 'custom',
  },
  {
    label: 'Price',
    subLabel: budgetType === 'approved' ? 'Approved' : 'Current',
    key: 'priceCurrent',
    flexSize: 1,
    editable: true,
    placeholder: '0.00',
    emptyValue: 0,
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
  {
    label: 'Units/Items',
    subLabel: 'Proposed',
    key: 'unitsProposed',
    flexSize: 1,
    editable: true,
    placeholder: '0',
    type: 'number',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
  {
    label: 'Reserve',
    subLabel: 'Proposed',
    key: 'reserveProposed',
    flexSize: 1,
    editable: true,
    placeholder: '0.00',
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'normal' ||
      budgetType === 'approved',
  },
  {
    label: 'Reserve',
    subLabel: 'Actual',
    key: 'reserveActual',
    flexSize: 0.8,
    placeholder: '0.00',
    emptyValue: 0,
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
  {
    label: 'Operating',
    subLabel: 'Proposed',
    key: 'operatingProposed',
    flexSize: 0.8,
    placeholder: '0.00',
    emptyValue: 0,
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
  {
    label: 'Operating',
    subLabel: 'Actual',
    key: 'operatingActual',
    flexSize: 0.8,
    placeholder: '0.00',
    emptyValue: 0,
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
  {
    label: 'Total Cost',
    subLabel: 'Actual',
    key: 'totalActual',
    flexSize: 0.8,
    placeholder: '0.00',
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'normal' ||
      budgetType === 'approved',
  },
  {
    label: 'Units/Items',
    subLabel: 'Total Actual',
    key: 'unitsActual',
    flexSize: 0.8,
    editable: true,
    placeholder: '0',
    type: 'text',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'normal' ||
      budgetType === 'approved',
  },
  {
    label: 'Comments',
    key: 'comments',
    textAlign: 'left',
    flexSize: 2,
    editable: true,
    placeholder: 'Comment',
    type: 'text',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
];

export const data: SectionConfiguration[] = [
  {
    header: 'Appliances',
    rows: [
      {
        label: 'Range',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Refrigerator',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Range Hood',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Washers & Dryers',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Carpet & Vinyl',
    rows: [
      {
        label: '1BR',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: '2BR',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: '3BR',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: '4BR',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Cabinets',
    rows: [
      {
        label: 'Kitchens',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Bathrooms',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Doors',
    rows: [
      {
        label: 'Exterior',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Interior',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Window Coverings',
    rows: [
      {
        label: 'List',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Heating & AC',
    rows: [
      {
        label: 'Heating',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Air Conditioning',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Plumbing',
    rows: [
      {
        label: 'Water Heater',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Bath Sinks',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Kitchen Sinks',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Faucets',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Toilets',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Major Electrical',
    rows: [
      {
        label: 'List',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Structures',
    rows: [
      {
        label: 'Windows',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Screens',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Walls',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Roofing',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Siding',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Exterior Painting',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Paving',
    rows: [
      {
        label: 'Asphalt',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Concrete',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Seal & Stripe',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Landscape & Grounds',
    rows: [
      {
        label: 'Landscaping',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Lawn Equipment',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Fencing',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Recreation Area',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Signs',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Accessibility features',
    rows: [
      {
        label: 'List',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Automation Equipment',
    rows: [
      {
        label: 'Site Management',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Common Area',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: 'Other',
    rows: [
      {
        label: 'Other List 1',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other List 2',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
      {
        label: 'Other List 3',
        reserveActual: '$0.00',
        operatingProposed: '$0.00',
        operatingActual: '$0.00',
        totalActual: '$0.00',
        unitsActual: '0',
      },
    ],
  },
  {
    header: null,
    headless: true,
    rows: [
      {
        label: 'Total Capital Expenses:',
        highlightAsHeader: true,
        disabledWhen: {
          priceCurrent: ['normal', 'approved', 'custom'],
          unitsProposed: ['normal', 'approved', 'custom'],
          reserveProposed: ['normal', 'approved', 'custom'],
          reserveActual: ['normal', 'approved', 'custom'],
          operatingProposed: ['normal', 'approved', 'custom'],
          operatingActual: ['normal', 'approved', 'custom'],
          totalActual: ['normal', 'approved', 'custom'],
          unitsActual: ['normal', 'approved', 'custom'],
          comments: ['approved'],
        },
      },
    ],
  },
];

const Part5 = ({
  useFormReturn,
  intacctDifferences,
  rentForecast,
  budgetType,
}) => {
  const [highlightFields, differenceCount] = useMemo(
    () => differencesToHighlight(intacctDifferences),
    [intacctDifferences],
  );
  const sections = useMemo(
    () => resolveBudgetSections(data, budgetType),
    [rentForecast],
  );
  const headers = generateHeaders(differenceCount, budgetType);

  const renderAll = getQueryStringValue('fullRenderTable') === 'true';

  return (
    <TableBudget
      fieldNamePrefix={'3'}
      columns={headers}
      sections={sections}
      useFormReturn={useFormReturn}
      tableBodySize={{ height: 800, width: 2330 }}
      highlightFields={highlightFields}
      renderAll={renderAll}
    />
  );
};

export default memo(Part5);
